<script setup lang="ts">
// import { useWalletStore } from '@/stores/walletStore';
import { PublicKey } from "@solana/web3.js";
import { computed, ref, watch, watchEffect, defineExpose } from "vue";
import FormField from "@/components/form/FormField.vue";
import FormGroup from "@/components/form/FormGroup.vue";
import { rules } from "@/components/form/rules";
import CommonSpinner from "@/components/common/CommonSpinner.vue";
import ErrorCard from "../common/ErrorCard.vue";
import { storage } from "@/composables/useStorage";
import { fogoSession, getExpiresDuration } from "@/composables/useFogoSession";
import {
  ONE_DAY_IN_MS,
  ONE_HOUR_IN_MS,
  ONE_MINUTE_IN_MS,
  ONE_WEEK_IN_MS,
} from "@/composables/useFogoSession";
import store from "@/store";
import { useWallet } from "@/composables/useWallet";
import { Network } from "@fogo/sessions-sdk";
import { SOL_MINT } from "@/types/constants";

// store
const walletStore = computed<ReturnType<typeof useWallet> | undefined>(() => {
  if (!store.state.wallet.wallet) return undefined;
  return store.state.wallet.wallet;
});
// const wallet = useWallet();

// computed
const getDefaultLimits = computed<{ [key: string]: string }>(() => {
  const t: { [key: string]: string } = {};
  const defaults: Record<string, string> = storage.get("FOGO_LIMITS", {});
  for (const token of tokens.value) {
    t[token.publicKey.toString()] = defaults[token.publicKey.toString()] || "";
  }
  return t;
});

// data
const tokens = ref<
  {
    publicKey: PublicKey;
    symbol: string;
    logo: string;
  }[]
>([
  {
    publicKey: SOL_MINT,
    symbol: "FOGO",
    logo: "/img/logo/fogo.svg",
  }, // FOGO token
  {
    publicKey: new PublicKey("C1e7Q5xwkD2fh5ForBTkfZbcBaukRe7SMr5TnCqbNfgR"),
    symbol: "Aeon",
    logo: "/img/logo/aeon.png",
  }, // aeon token
]);
const limits = ref<Map<PublicKey, number>>(new Map());
const opened = ref<boolean>(false);
const loading = ref<boolean>(false);
const err = ref<Error>();

// methods
function requestLimits() {
  if (opened.value) return;
  opened.value = true;
}
async function connectFogoWallet({
  models,
  isValid,
}: {
  models: { [key: string]: string };
  isValid: boolean;
}) {
  try {
    if (!isValid) return;
    if (!walletStore.value || !walletStore.value.wallet) {
      throw new Error("Wallet not connected");
    }

    const expires = getExpiresDuration(Number(models.expires));
    const onlyLimits = { ...models };
    delete onlyLimits.expires;

    if (!models.skipStorage) {
      storage.set("FOGO_LIMITS", onlyLimits, expires);
    }

    for (const [publicKey, limit] of Object.entries(onlyLimits)) {
      const n = Number(limit);
      if (isNaN(n) || n === 0) continue;
      limits.value.set(new PublicKey(publicKey), Number(limit));
    }

    loading.value = true;
    err.value = undefined;
    
    const { error, data } = await fogoSession.getOrEstablishSession({
      wallet: {
        // @ts-expect-error
        publicKey: walletStore.value.wallet.publicKey,
        // @ts-expect-error
        signMessage: walletStore.value.wallet.signMessage,
      },
      options: {
        domain: "https://chronokingdoms.com",
        network: Network.Testnet,
        duration: Number(models.expires),
        // @ts-expect-error i think fogo uses bigint wrongly, because we should accept decimals number
        limits: limits.value,
        expires,
      },
    });
    loading.value = false;

    if (error) {
      throw error;
    }

    opened.value = false;

    store.commit("user/set_fogo_session", {
      session: data.session,
      context: data.context,
      wallet: store.state.wallet.wallet,
    });
    store.commit("user/set_needs_fogo_reconnect", false);
    // TODO: we should enable this to load user worlds on fogo login,
    // const worlds = await store.state.user.client.indexWorlds()

    // if(worlds.length > 0) {
    //   store.commit("user/set_world", worlds[0].publicKey)
    //   store.commit("village/set_world", worlds[0].account)
    // }
  } catch (error) {
    console.error("[FogoWallet] Failed to connect:", error);
    err.value = error as Error;
    fogoSession.disconnect();
  } finally {
    loading.value = false;
  }
}
async function checkIfShouldAutoConnect() {
  if (!walletStore.value?.wallet.connected) return;
  if (store.state.user.needsFogoReconnect) return;
  if (opened.value) return;

  if (store.state.user.session && !fogoSession.isExpired()) {
    return;
  }

  const models: Record<string, string> = storage.get("FOGO_LIMITS", {});
  const noLimits = Object.keys(models).length === 0;

  if (noLimits) {
    requestLimits();
    return;
  }

  models.expires = ONE_WEEK_IN_MS.toString();
  models.skipStorage = "true";

  await connectFogoWallet({
    isValid: true,
    models,
  });
}

// watch
watchEffect(() => {
  if (!walletStore.value?.wallet.connected) return;
  if (store.state.user.needsFogoReconnect) return;
  if (opened.value) return;
  
  checkIfShouldAutoConnect();
});

watch(
  () => store.state.user.needsFogoReconnect,
  (needsReconnect) => {
    if (!needsReconnect) return;
    if (!walletStore.value?.wallet.connected) return;
    
    store.commit("user/clear_fogo_session");
    requestLimits();
    // Flag will be cleared in connectFogoWallet after success
  },
  { immediate: true }
);

// expose
defineExpose({
  requestLimits,
  connectFogoWallet,
  checkIfShouldAutoConnect,
  tokens,
  getDefaultLimits,
  opened,
  err,
  loading,
});
</script>

<template>
  <dialog
    v-if="walletStore?.wallet.connected"
    :open="opened"
    class="modal static-backdrop"
  >
    <article
      class="card card-content"
      style="
        width: 500px;
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 20px;
        position: relative;
        background-color: #0198cb;
        border: none;
      "
    >
      <!-- header -->
      <img
        src="/img/logo/fogo-white.svg"
        alt="Fogo Logo"
        style="width: 80px; margin: 16px 0; opacity: 0.5"
      />

      <h3 class="mt-4 text-center" style="color: #fff !important">
        Session Limits
      </h3>
      <p class="text-secondary text-center text-white">
        Limit how many tokens this app is allowed to interact with
      </p>

      <!-- fields -->
      <FormGroup
        v-slot="{ models, isValid }"
        style="
          width: 100%;
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-top: 20px;
        "
        :default="{ ...getDefaultLimits, expires: ONE_WEEK_IN_MS.toString() }"
      >
        <FormField
          v-for="token in tokens"
          :key="token.publicKey.toString()"
          v-model="models[token.publicKey.toString()]"
          :rules="[rules.fogoLimits]"
        >
          <div class="input-group input">
            <div style="margin-right: 10px">
              <img
                :src="token.logo"
                :alt="token.symbol"
                style="width: 24px; border-radius: 9999px"
              />
            </div>
            <input
              v-model.trim="models[token.publicKey.toString()]"
              type="text"
              min="0"
              class="input"
              placeholder="Unlimited"
              :disabled="loading"
            />
            <span class="text-secondary text-white">{{ token.symbol }}</span>
          </div>
        </FormField>

        <FormField v-model="models.expires">
          <div class="input input-group text-white">
            <span style="margin-right: 10px">Allow for</span>
            <select v-model="models.expires" class="input text-white">
              <option :value="ONE_MINUTE_IN_MS.toString()">1 minute</option>
              <option :value="ONE_HOUR_IN_MS.toString()">1 hour</option>
              <option :value="ONE_DAY_IN_MS.toString()">1 day</option>
              <option :value="ONE_WEEK_IN_MS.toString()">1 week</option>
            </select>
          </div>
        </FormField>

        <!-- error -->
        <ErrorCard @close="err = undefined" :err="err">
          <span class="font-semibold"> Error to create fogo session </span>
        </ErrorCard>

        <!-- submit -->
        <button
          @click="connectFogoWallet({ models, isValid })"
          :disabled="!isValid || loading"
          class="btn btn-primary mt-8 w-full"
        >
          <CommonSpinner v-if="loading" class="inline" />
          <span v-else>Login</span>
        </button>
      </FormGroup>
    </article>
  </dialog>
</template>

<style scoped>
a {
  background-color: #bce0ef;
}

input::placeholder {
  color: white;
}
</style>
